import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse, PaginatedApiResponse } from '@card-software/library';
import { environment } from '../../../environments/environment';
import {
  CreateLotItemRelocationCommand,
  LotItemRelocationDestinationCommand,
  LotItemRelocationDto,
  LotItemRelocationScanCommand,
  LotItemRelocationScanResultDto,
  LotItemRelocationSearchCommand,
} from '../../models/inventory/lot-item-relocation';
import { getSkipperErrorHeader } from '../../utils/error-header.util';

export interface RelocationRequestOptions {
  skipErrorToast?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LotItemRelocationProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/lot-item-relocation';

  public getById(id: string): Observable<ApiResponse<LotItemRelocationDto>> {
    return this.httpClient.get<ApiResponse<LotItemRelocationDto>>(`${this.baseUrl}/${id}`);
  }

  public search(
    searchCommand: LotItemRelocationSearchCommand,
    pageNumber = 1,
    pageSize = 25,
  ): Observable<ApiResponse<PaginatedApiResponse<LotItemRelocationDto>>> {
    const queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<LotItemRelocationDto>>>(
      this.baseUrl + '/search' + queryParams,
      searchCommand,
    );
  }

  public create(command: CreateLotItemRelocationCommand): Observable<ApiResponse<LotItemRelocationDto>> {
    return this.httpClient.post<ApiResponse<LotItemRelocationDto>>(this.baseUrl, command);
  }

  public scan(
    id: string,
    command: LotItemRelocationScanCommand,
    options?: RelocationRequestOptions,
  ): Observable<ApiResponse<LotItemRelocationScanResultDto>> {
    return this.httpClient.post<ApiResponse<LotItemRelocationScanResultDto>>(`${this.baseUrl}/${id}/scan`, command, {
      headers: getSkipperErrorHeader(!!options?.skipErrorToast),
    });
  }

  public removeLine(id: string, lotItemNumber: string): Observable<ApiResponse<null>> {
    return this.httpClient.delete<ApiResponse<null>>(`${this.baseUrl}/${id}/line/${encodeURIComponent(lotItemNumber)}`);
  }

  public updateDestination(
    id: string,
    command: LotItemRelocationDestinationCommand,
    options?: RelocationRequestOptions,
  ): Observable<ApiResponse<LotItemRelocationDto>> {
    return this.httpClient.put<ApiResponse<LotItemRelocationDto>>(`${this.baseUrl}/${id}/destination`, command, {
      headers: getSkipperErrorHeader(!!options?.skipErrorToast),
    });
  }

  public confirm(id: string, options?: RelocationRequestOptions): Observable<ApiResponse<LotItemRelocationDto>> {
    return this.httpClient.post<ApiResponse<LotItemRelocationDto>>(
      `${this.baseUrl}/${id}/confirm`,
      {},
      { headers: getSkipperErrorHeader(!!options?.skipErrorToast) },
    );
  }
}
